import type { LoadPlanningCurveDataPointDTO } from '@/src/api/types/procurement'
import { FlexCol, FlexRow } from '@/src/components'
import { BaseText } from '@/src/components/BaseText'
import type { GranularityEnum } from '@/src/enums/granularity.enum'
import {
  type LoadPlanningCurveDataPointValueProperties,
  getColor,
  getEnergyUnitType,
  getPropertyForLeftBar,
  getPropertyForRightBar,
  mapLoadPlanningUnitText,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-graph/LoadPlanning.utils'
import { formatPortfolioTooltipTimestamp } from '@/src/features/procurement-and-markets/portfolio/components/diagram/utils/portfolioDiagram.utils'
import { appColors } from '@/src/theme'
import { formatMeasurement } from '@/src/utils/formatMeasurement'
import { Box } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import type { TooltipProps } from 'recharts'

interface LoadPlanningGraphTooltipProps extends TooltipProps<number, string> {
  granularity: GranularityEnum
}

export const LoadPlanningGraphTooltip = ({ active, payload, granularity }: LoadPlanningGraphTooltipProps) => {
  const { t } = useTranslation()

  if (!active || !payload) {
    return null
  }
  const [leftBar] = payload

  // I have to do casting since internally the payload is stored as `any` type and no way to define the payload type through the generics
  const dataPoint = leftBar?.payload as LoadPlanningCurveDataPointDTO | undefined

  if (!dataPoint) {
    return null
  }

  const firstTooltipProperty = getPropertyForLeftBar(dataPoint, granularity)
  const secondTooltipProperty = getPropertyForRightBar(dataPoint, granularity)

  return (
    <FlexCol
      borderRadius={8}
      borderColor='gray.100'
      borderWidth='2px'
      className='custom-tooltip'
      backgroundColor='white'
      padding={3}
      opacity={'0.95'}
      width={'300px'}
      gap='4px'
    >
      <BaseText color={appColors.secondaryForeground}>{formatPortfolioTooltipTimestamp(dataPoint.timestampStart, granularity, t)}</BaseText>
      <TooltipRow keyName={firstTooltipProperty} data={dataPoint} granularity={granularity} />
      <TooltipRow keyName={secondTooltipProperty} data={dataPoint} granularity={granularity} />
    </FlexCol>
  )
}

const TOOLTIP_LABEL_MAP: Record<keyof LoadPlanningCurveDataPointValueProperties, string> = {
  actualConsumptionValue: 'components.procurement.forecasting.graph.tooltip.actual_consumption',
  savedValue: 'components.procurement.forecasting.graph.tooltip.saved_forecast',
  draftValue: 'components.procurement.forecasting.graph.tooltip.draft_forecast',
}

const TooltipRow = ({
  keyName,
  data,
  granularity,
}: {
  keyName: keyof LoadPlanningCurveDataPointValueProperties
  data?: LoadPlanningCurveDataPointDTO
  granularity: GranularityEnum
}) => {
  const { t } = useTranslation()

  if (!data?.[keyName]) return null
  const unitProperty = getEnergyUnitType(granularity)

  const valueData = data[keyName]
  if (!valueData) return null

  const amountInfo = valueData[unitProperty]

  return (
    <FlexRow justifyContent={'space-between'} alignItems={'center'}>
      <FlexRow alignItems={'center'} gap={'4px'}>
        <Box w={'12px'} h={'12px'} bg={getColor(keyName)} borderRadius={'4px'} />
        <BaseText>{t(TOOLTIP_LABEL_MAP[keyName])}</BaseText>
      </FlexRow>
      <BaseText fontWeight={116}>{formatMeasurement(amountInfo.amount, mapLoadPlanningUnitText(amountInfo.unit) ?? 'kWh')}</BaseText>
    </FlexRow>
  )
}
